import { useState, type SyntheticEvent } from "react";
import { TextInput } from "@sprout/text_input.tsx";
import { SecondaryButton } from "@sprout/buttons";
import { DestroyableTag } from "@sprout/tag";
import useTranslation from "@hooks/use_translation.ts";
import ModalFooter from "./modal_footer.tsx";
import useSectionSave from "./use_section_save.ts";
import type { SectionProps } from "./types.ts";

const MAX_SKILLS = 25;
const SKILL_MAX_LENGTH = 255;

const SkillsSection = ({ data, onClose, source }: SectionProps) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_checklist.modals.skills" });
  const { save, saving, errors } = useSectionSave();

  const [skills, setSkills] = useState<string[]>(data.skills ?? []);
  const [newSkill, setNewSkill] = useState("");
  const [skillError, setSkillError] = useState<string | null>(null);

  const addSkill = (event: SyntheticEvent) => {
    event.preventDefault();

    if (skills.length >= MAX_SKILLS) {
      setSkillError(t("limit_reached"));
      return;
    }

    const trimmed = newSkill.trim();
    const alreadyExists = skills.some((skill) => skill.toLowerCase() === trimmed.toLowerCase());
    setNewSkill("");
    setSkillError(null);

    if (trimmed.length === 0 || alreadyExists) return;

    setSkills((previous) => previous.concat([trimmed]));
  };

  const removeSkill = (skill: string) => {
    setSkills((previous) => previous.filter((existing) => existing !== skill));
    setSkillError(null);
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("skills", JSON.stringify(skills));

    const success = await save(formData);
    if (success) onClose();
    return success;
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-start gap-3">
          <div className="min-w-0 flex-1">
            <TextInput
              id="skills-input"
              label={null}
              placeholder={t("placeholder")}
              value={newSkill}
              maxLength={SKILL_MAX_LENGTH}
              short
              narrow
              onChange={(e) => setNewSkill(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addSkill(e);
              }}
              error={skillError ?? errors.skills?.[0]}
            />
          </div>
          <SecondaryButton size="medium" onClick={addSkill} disabled={!newSkill.trim()}>
            {t("add")}
          </SecondaryButton>
        </div>
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <DestroyableTag
              key={skill}
              label={skill}
              size="md"
              truncate={false}
              maxWidth="max-w-full"
              onClick={() => removeSkill(skill)}
            />
          ))}
        </div>
      </div>
      <ModalFooter
        section="skills"
        onSave={handleSave}
        onCancel={onClose}
        saving={saving}
        source={source}
      />
    </>
  );
};

export default SkillsSection;
